<script setup lang="ts">
import {
    AlertMessage,
    ButtonComp,
    ButtonGroup,
    ContentComp,
    DataSourceLookup,
    InputDateTime,
    InputNullableDateTime,
    InputSelect,
    LoadingComp,
    ToolbarComp,
} from '@/components';
import { GridColumnDefinition, GridColumnOptions, GridDataSource, GridSetup } from '@/components/Grid/Classes';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import type { IGridRowCssRule } from '@/components/Grid/Classes/IGridRowCssRule';
import GridComp from '@/components/Grid/GridComp.vue';
import { useDialog, useKeyboardListener, useLoaders, useTaskLoader } from '@/composeables';
import { BaseObjDao, EventImplementationDao, ReportDao } from '@/dao';
import { DropdownItemButton, DropdownItemMenu, DropdownItemSeparator, type SelectLineValueType } from '@/objects';
import {
    RaceContestantTime,
    EventImplementation,
    type EventImplementationType,
    DynamicReportRequest,
    DynamicReportParameter,
    Report,
    ContestantBase,
    RaceContestant,
} from '@/objects/generated';
import { RaceControlItem } from '@/objects/RaceControlItem';
import {
    useContestantStore,
    useContestantStoreRef,
    useContestantUpdateSubscription,
    useEventCommonStoreRef,
    useEventStoreRef,
    useRaceContestantTimesSubscription,
    useRaceContestantTimeStore,
    useRaceControlStore,
    useReportStoreRef,
    useToastStore,
    useUtilStore,
} from '@/stores';
import { arrayUnique, propertyof } from '@/utilities';
import { computed, onMounted, ref, watch } from 'vue';
import type { ComponentExposed } from 'vue-component-type-helpers';
import { addTimeToDate } from '@/utilities/dateutils';
import type { ISelectLineDatasource } from '@/objects/ISelectLineDatasource';
import PrintResult from './PrintResult.vue';
import { vElementVisibility } from '@vueuse/components';
import { watchDebounced } from '@vueuse/core';
import FinishedProgress from './FinishedProgress.vue';
import StartedProgress from './StartedProgress.vue';
import { showStreamInNewWindow } from '@/utilities/utils';
import { useDebouncer } from '@/utilities/Debouncer';

const grid = ref<ComponentExposed<typeof GridComp<any>> | null>(null);

const { eventID } = useEventStoreRef();

const { AddLoader, LoadState } = useTaskLoader();
useLoaders(AddLoader, ['CATEGORIES', 'GROUPS', 'RACES', 'STATIONROLES', 'CONTESTANTS', 'EVENTIMPLEMENTATIONS', 'ACTIVETIMES']);

const { AddLoader: PrintAddLoader, LoadState: PrintState } = useTaskLoader();

const eventCommon = useEventCommonStoreRef();
const raceContestantTimeStore = useRaceContestantTimeStore();
const { EventImplementations, StationRoles, Categories, Races, Groups } = useEventCommonStoreRef();
const { IsLoaded: ContestantsIsLoaded } = useContestantStoreRef();
const { ManagerReportsForEvent, Reports } = useReportStoreRef();

const contestantStore = useContestantStore();

const { Enable: EnableRaceControl } = useRaceControlStore();

// We use this one to switch the loader to a loader on top of content
const hasLoadedData = ref(false);

// We toggle this one to show and hide empty rows
const hideRowsWithoutContestants = ref(false);

// Contains the current search text
const searchText = ref('');

// Whenever the data is changed we need to show a warning
const isChanged = ref(false);

/**
 * The emits
 */
const emits = defineEmits<{
    (e: 'categoryCompleted', value: RaceControlItem): void;
}>();

/**
 * The row class rules for the grid
 * We want to show the current active times as Green and the times that are not active as red. Disabled times we show as grey
 */
const rowClassRules: IGridRowCssRule<RaceControlItem>[] = [
    {
        className: 'grid-row-warning',
        func: (data: RaceControlItem) => data.NumberOfFinished + data.NumberOfDNX > data.NumberOfStarted,
    },
    {
        className: 'grid-row-success',
        func: (data: RaceControlItem) => {
            return data.NumberOfFinished + data.NumberOfDNX == data.NumberOfStarted && data.NumberOfStarted > 0;
        },
    },
];

/**
 * We will build the columns for the grid
 */
const columns: GridColumnOptions<RaceControlItem>[] = [];
{
    new GridColumnOptionsBuilder<RaceControlItem>(propertyof<RaceControlItem>('CategoryID'), 'general.category')
        .ReadOnly()
        .SortDisabled()
        .Filterable(() => {
            let uniqueValues = arrayUnique(rows.value.map((p) => p.CategoryID));
            // Convert to a map with the same key and value
            return new Map<string, any>(uniqueValues.map((x) => [x.toString(), x]));
        })
        .CellRenderer(DataSourceLookup, { hideCaret: true, datasource: { mapsource: eventCommon.CategoriesAsSelectLine } })
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<RaceControlItem>(propertyof<RaceControlItem>('RaceID'), 'general.race')
        .ReadOnly()
        .Filterable(() => {
            let uniqueValues = arrayUnique(rows.value.map((p) => p.RaceID));

            return new Map<string, any>(uniqueValues.map((x) => [x.toString(), x]));
        })
        .SortDisabled()
        .CellRenderer(DataSourceLookup, { hideCaret: true, datasource: { mapsource: eventCommon.RacesAsSelectLine } })
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<RaceControlItem>(
        propertyof<RaceControlItem>('NumContestants'),
        'groupwithcount.numcontestants',
        undefined,
        'number',
    )
        .ReadOnly()
        .SortDisabled()
        .Size(100, 100)
        .Aggregate({ type: 'sum', aggregate: () => computedRows.value.reduce((acc, row) => acc + row.NumContestants, 0) })
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<RaceControlItem>(propertyof<RaceControlItem>('NumberOfStarted'), 'general.numstarted', undefined, 'number')
        .ReadOnly()
        .CellRenderer(StartedProgress)
        .SortDisabled()
        .Size(150, 150)
        .Aggregate({ type: 'sum', aggregate: () => computedRows.value.reduce((acc, row) => acc + row.NumberOfStarted, 0) })
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<RaceControlItem>(
        propertyof<RaceControlItem>('NumberOfFinished'),
        'general.numfinished',
        undefined,
        'number',
    )
        .ReadOnly()
        .CellRenderer(FinishedProgress)
        .SortDisabled()
        .Size(150, 150)
        .Aggregate({ type: 'sum', aggregate: () => computedRows.value.reduce((acc, row) => acc + row.NumberOfFinished, 0) })
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<RaceControlItem>(propertyof<RaceControlItem>('NumberOfDNX'), 'general.numdnx', undefined, 'number')
        .ReadOnly()
        .SortDisabled()
        .Size(80, 80)
        .Aggregate({ type: 'sum', aggregate: () => computedRows.value.reduce((acc, row) => acc + row.NumberOfDNX, 0) })
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<RaceControlItem>(propertyof<RaceControlItem>('LastFinished'), 'general.lastfinished', undefined, 'time')
        .ReadOnly()
        .SortDisabled()
        .Size(100, 100)
        .Aggregate({ type: 'max', aggregate: () => new Date(Math.max(...computedRows.value.map((x) => x.LastFinished?.getTime() ?? 0))) })
        .InsertAtEnd(columns);

    let appealcol = new GridColumnOptionsBuilder<RaceControlItem>(
        propertyof<RaceControlItem>('AppealDeadline'),
        'racecontrol.appealdeadline',
        'AppealDeadline',
        'time',
    )
        .CellEditor(InputNullableDateTime, { format: 'TIME' })
        .SortDisabled()
        .Size(120, 120)
        .Build();

    appealcol.valueGetter = (rowdata: RaceControlItem) => {
        return rowdata.AppealDeadline?.TimeValue ?? null;
    };

    appealcol.valueSetter = async (rowdata: RaceControlItem, value: Date) => {
        if (value != null) {
            rowdata.AppealDeadline = UpdateEventImplementationItem(
                'AppealDeadline',
                rowdata.CategoryID,
                rowdata.RaceID,
                rowdata.AppealDeadline,
                addTimeToDate(value, new Date()),
            );
            isChanged.value = true;
        }

        return true;
    };

    columns.push(appealcol);

    const officialDatasource = {
        source: [
            { value: null, text: 'general.notset' },
            { value: true, text: 'general.official' },
            { value: false, text: 'general.unofficial' },
        ],
    } as ISelectLineDatasource;

    const offuncol = new GridColumnOptionsBuilder<RaceControlItem>(
        propertyof<RaceControlItem>('OfficialUnoffical'),
        'racecontrol.officialunofficial',
    )
        .CellRenderer(DataSourceLookup, { hideCaret: true, datasource: officialDatasource })
        .CellEditor(InputSelect, { hideCaret: true, datasource: officialDatasource })
        .Size(120, 120)
        .SortDisabled()
        .Build();
    offuncol.valueGetter = (rowdata: RaceControlItem) => {
        return rowdata.OfficialUnoffical?.BooleanValue ?? null;
    };

    offuncol.valueSetter = async (rowdata: RaceControlItem, value: SelectLineValueType) => {
        if (value != null) {
            rowdata.OfficialUnoffical = UpdateEventImplementationItem(
                'OfficialLive',
                rowdata.CategoryID,
                rowdata.RaceID,
                rowdata.OfficialUnoffical,
                null,
                value as boolean,
            );

            isChanged.value = true;
        }
        return true;
    };
    columns.push(offuncol);

    let officialprinted = new GridColumnOptionsBuilder<RaceControlItem>(
        propertyof<RaceControlItem>('OfficalPrinted'),
        'racecontrol.officalprinted',
        'OfficalPrinted',
        'time',
    )
        .ReadOnly()
        .CellRenderer(PrintResult, {
            type: 'OfficialPrinted',
            onPrint: (value: RaceControlItem | null, type: EventImplementationType) => {
                if (value) {
                    printResultsAction([value.RaceID], [value.CategoryID], type, value);
                }
            },
        })
        .SortDisabled()
        .Size(150, 150)
        .Build();

    columns.push(officialprinted);

    let unofficialprinted = new GridColumnOptionsBuilder<RaceControlItem>(
        propertyof<RaceControlItem>('UnofficialPrinted'),
        'racecontrol.unofficalprinted',
        'UnofficialPrinted',
        'time',
    )
        .ReadOnly()
        .CellRenderer(PrintResult, {
            type: 'UnofficialPrinted',
            onPrint: (value: RaceControlItem | null, type: EventImplementationType) => {
                if (value) {
                    printResultsAction([value.RaceID], [value.CategoryID], type, value);
                }
            },
        })
        .SortDisabled()
        .Size(150, 150)
        .Build();

    columns.push(unofficialprinted);
}

/**
 * The datasource for the grid
 */
const dataSource = new GridDataSource<RaceControlItem>({
    rowGetter: (p) => {
        let filtered = p.filter(computedRows.value);

        const slice = filtered.slice(p.startRow, p.endRow);
        return p.successCallback(slice, filtered, filtered.length);
    },
    rowCountGetter: () => computedRows.value.length,
});

/**
 * The setup for the grid
 */
const gridSetup = new GridSetup<RaceControlItem>({
    datasource: dataSource,
    rowSelection: 'multiple',
    hasCheckboxSelectionColumn: true,
    GridColumnDefinition: new GridColumnDefinition({ ColumnOptions: columns }),
    rowIdGetter: (data: RaceControlItem) => data.ID ?? `${data.RaceID}-${data.CategoryID}`,
    rowClassRules: rowClassRules,
    hideStatusbar: false,
});

// A list of all the race contestants
// const raceContestants = ref<RaceContestant[]>([]);

// The rows of the grid
const rows = ref<RaceControlItem[]>([]);

/**
 * Load the data for the view
 */
const loadData = async () => {
    buildDataset();

    grid.value?.purgeCachedData();
};

/**
 * Rows to display with the current filters applied
 */
const computedRows = computed(() => {
    let _rows = rows.value;
    if (hideRowsWithoutContestants.value) {
        _rows = _rows.filter((x) => x.NumberOfStarted > 0);
    }

    if (searchText.value != '') {
        _rows = _rows.filter((x) => {
            let category = eventCommon.Categories.value.find((c) => c.ID == x.CategoryID);

            if (category?.Name.toLocaleLowerCase().includes(searchText.value.toLocaleLowerCase())) {
                return true;
            }

            let race = eventCommon.Races.value.find((r) => r.ID == x.RaceID);
            if (race?.Name?.toLocaleLowerCase().includes(searchText.value.toLocaleLowerCase())) {
                return true;
            }
            return false;
        });
    }

    return _rows;
});

type OldRowSnapshot = {
    AppealDeadlineTime: string | null | undefined;
    OfficalPrintedTime: string | null | undefined;
    UnofficialPrintedTime: string | null | undefined;
    OfficialBool: boolean | null | undefined;
};

/**
 *
 * @param raceId
 * @param categoryId
 * @param changedRowColumns
 * @param oldData
 */
const buildRaceControlItem = (
    raceId: number,
    categoryId: number,
    changedRowColumns: Map<string, string[]>,
    oldData: Map<string, OldRowSnapshot>,
) => {
    const item = new RaceControlItem();
    item.CategoryID = categoryId;
    item.EventID = eventID.value;
    item.RaceID = raceId;
    item.ID = `${item.RaceID}-${item.CategoryID}`;
    item.NumContestants = 0;
    item.NumberOfFinished = 0;
    item.NumberOfDNX = 0;
    item.NumberOfStarted = 0;
    item.LastFinished = null;

    item.AppealDeadline =
        EventImplementations.value.find(
            (ei) => ei.CategoryID == item.CategoryID && ei.RaceID == item.RaceID && ei.TypeID == 'AppealDeadline',
        ) ?? null;

    item.OfficalPrinted =
        EventImplementations.value.find(
            (ei) => ei.CategoryID == item.CategoryID && ei.RaceID == item.RaceID && ei.TypeID == 'OfficialPrinted',
        ) ?? null;

    item.UnofficialPrinted =
        EventImplementations.value.find(
            (ei) => ei.CategoryID == item.CategoryID && ei.RaceID == item.RaceID && ei.TypeID == 'UnofficialPrinted',
        ) ?? null;

    item.OfficialUnoffical =
        EventImplementations.value.find(
            (ei) => ei.CategoryID == item.CategoryID && ei.RaceID == item.RaceID && ei.TypeID == 'OfficialLive',
        ) ?? null;

    // We handle the changes to the row here
    if (!changedRowColumns.has(item.ID)) {
        changedRowColumns.set(item.ID, []);
    }

    const oldValue = item.ID != null ? oldData.get(item.ID) : undefined;
    if (oldValue != null) {
        if (
            item.AppealDeadline?.TimeValue !== undefined &&
            oldValue.AppealDeadlineTime != JSON.parse(JSON.stringify(item.AppealDeadline?.TimeValue))
        ) {
            changedRowColumns.get(item.ID)?.push('AppealDeadline');
        }

        if (
            item.OfficalPrinted?.TimeValue !== undefined &&
            oldValue.OfficalPrintedTime != JSON.parse(JSON.stringify(item.OfficalPrinted?.TimeValue))
        ) {
            changedRowColumns.get(item.ID)?.push('OfficalPrinted');
        }

        if (
            item.UnofficialPrinted?.TimeValue !== undefined &&
            oldValue.UnofficialPrintedTime != JSON.parse(JSON.stringify(item.UnofficialPrinted?.TimeValue))
        ) {
            changedRowColumns.get(item.ID)?.push('UnofficialPrinted');
        }

        if (item.OfficialUnoffical?.BooleanValue !== undefined && oldValue.OfficialBool != item.OfficialUnoffical?.BooleanValue) {
            changedRowColumns.get(item.ID)?.push('OfficialUnoffical');
        }
    }

    const rowdataDetails = getRowdataDetails(raceId, categoryId);
    item.NumContestants = rowdataDetails.numberOfContestants;
    item.NumberOfStarted = rowdataDetails.numberOfStarted;
    item.NumberOfDNX = rowdataDetails.numberOfDnx;
    item.NumberOfFinished = rowdataDetails.numberOfFinished;
    item.LastFinished = rowdataDetails.lastFinished;
    item.StartingTimes = Array.from(rowdataDetails.startingTimes).sort((a, b) => a.getTime() - b.getTime());

    return item;
};

/**
 * Internal helper class for all the details we want to show for a row
 */
class rowDataDetails {
    numberOfStarted: number = 0;

    numberOfContestants: number = 0;

    numberOfDnx: number = 0;

    numberOfFinished: number = 0;

    lastFinished: Date | null = null;

    startingTimes: Set<Date> = new Set();

    public construct() {}
}

/**
 * Get all the correct data for the row
 * @param raceId
 * @param categoryId
 */
const getRowdataDetails = (raceId: number, categoryId: number): rowDataDetails => {
    const rowdata = new rowDataDetails();

    // We get all the contestants for the category
    const contestants = contestantStore.GetCategoryContestants(categoryId);
    const raceContestantsInThis = contestants.flatMap((c) =>
        c.RaceContestants.filter((rc) => rc.RaceID == raceId && contestantStore.VisibleContestantStatuses.includes(rc.ContestantStatusID)),
    );

    // We need to make sure there is a racecontestant in the race
    for (const raceContestant of raceContestantsInThis) {
        // We need a list of all the unique starting times for the row
        if (raceContestant.StartingTime != null) {
            rowdata.startingTimes.add(raceContestant.StartingTime);
        }

        rowdata.numberOfContestants++;

        if (raceContestant.StartingTime != null && raceContestant.StartingTime <= new Date()) {
            rowdata.numberOfStarted++;
        }

        if (raceContestant.DNF || raceContestant.DSQ || raceContestant.DNQ || raceContestant.DNS) {
            rowdata.numberOfDnx++;
        } else {
            // Get the contestant times for the current racecontestant
            const rctimes = raceContestantTimeStore.GetTimesForRaceContestantId(raceContestant.ID);

            if (rctimes?.length > 0) {
                // Get the time for the stationroleid finish
                let finishTime = rctimes.find((t) => finishStationRoles.value.has(t.StationRoleID));

                // TODO: Here it will be an issue with LAPs since we pass finish multiple times.

                if (finishTime != null) {
                    // We have a finish time
                    rowdata.numberOfFinished++;

                    rowdata.lastFinished =
                        finishTime.ClockTime > (rowdata.lastFinished ?? new Date(0)) ? finishTime.ClockTime : rowdata.lastFinished;
                }
            }
        }
    }
    return rowdata;
};

/**
 * This will build the dataset for the grid
 * This cannot be a computed since we are doing changes to the data
 */
const buildDataset = async () => {
    await raceContestantTimeStore.EnsureLoaded();
    await contestantStore.EnsureLoaded(['Contestants']);

    const _data: RaceControlItem[] = [];

    const changedRowColumns = new Map<string, string[]>();

    // We need to keep track of the rowIds and fieldIds that have changed
    const oldData = new Map<string, OldRowSnapshot>(
        rows.value
            .filter((x) => x.ID != null)
            .map((x) => [
                x.ID!,
                {
                    AppealDeadlineTime:
                        x.AppealDeadline?.TimeValue != null ? JSON.parse(JSON.stringify(x.AppealDeadline.TimeValue)) : undefined,
                    OfficalPrintedTime:
                        x.OfficalPrinted?.TimeValue != null ? JSON.parse(JSON.stringify(x.OfficalPrinted.TimeValue)) : undefined,
                    UnofficialPrintedTime:
                        x.UnofficialPrinted?.TimeValue != null ? JSON.parse(JSON.stringify(x.UnofficialPrinted.TimeValue)) : undefined,
                    OfficialBool: x.OfficialUnoffical?.BooleanValue,
                },
            ]),
    );

    let keys: string[] = [];

    // TODO: Add heatplans to filter.

    // We need all the combinations of Category and Group to get the CategoryID-RaceID pairs
    Categories.value.forEach((category) => {
        if (category.CategoryGroupConnection?.length == 0) {
            Races.value.forEach((race) => {
                let item = buildRaceControlItem(race.ID, category.ID, changedRowColumns, oldData);

                if (item.ID == null || keys.includes(item.ID)) {
                    return;
                }
                keys.push(item.ID);
                _data.push(item);
            });
        } else {
            category.CategoryGroupConnection.forEach((gc) => {
                let group = Groups.value.find((x) => x.ID == gc.GroupID);
                let item = buildRaceControlItem(group?.RaceID ?? 0, category.ID, changedRowColumns, oldData);

                // item.NumContestants = 0;
                if (item.ID == null || keys.includes(item.ID)) {
                    return;
                }
                keys.push(item.ID);
                _data.push(item);
            });
        }
    });

    rows.value = _data;

    grid.value?.refreshCachedData();

    changedRowColumns.forEach((value, key) => {
        if (value.length > 0) {
            grid.value?.flashCells([key], value);
        }
    });

    // We start the new startingtimehandler
    handleStartingTimes();
};

let updateStartingTimesTimerId: number | null = null;

/**
 * We need to iterate over all the starting times and find the next starting time for each row and build a new dataset for the row
 */
const handleStartingTimes = () => {
    const now = new Date();

    // We need to know when we are running a new timer. so we need to find the next startingtime
    let nextStartingtime: Date | null = null;

    for (const row of rows.value) {
        // We need to find the next starting time for the row
        for (const stime of row.StartingTimes) {
            if (stime.getTime() > now.getTime()) {
                if (nextStartingtime == null || stime.getTime() < nextStartingtime.getTime()) {
                    nextStartingtime = stime;
                }
                break;
            }
        }
    }

    // Find the next startting in seconds
    const nextStartingInMs = nextStartingtime != null ? Math.floor(nextStartingtime.getTime() - now.getTime()) : null;

    if (updateStartingTimesTimerId != null) {
        clearTimeout(updateStartingTimesTimerId);
        updateStartingTimesTimerId = null;
    }

    if (nextStartingInMs != null) {
        updateStartingTimesTimerId = window.setTimeout(() => {
            if (updateStartingTimesTimerId != null) {
                clearTimeout(updateStartingTimesTimerId);
                updateStartingTimesTimerId = null;
            }
            // We need to rebuild the dataset
            buildDataset();
        }, nextStartingInMs + 10000); // We add ten second to make sure we are past the starting time
    }
};

/**
 * Print the report
 * @param raceIds
 * @param categoryIds
 * @param type
 * @param report
 */
const printReport = async (raceIds: number[], categoryIds: number[], type: EventImplementationType, report: Report) => {
    //  can have multiple of the same race and category so we need to make them unique
    raceIds = arrayUnique(raceIds);
    categoryIds = arrayUnique(categoryIds);

    await PrintAddLoader(async () => {
        // We need to grab all the selected rows and build a correct filter and print thereport
        const req = DynamicReportRequest.FromJsValues({ ReportID: report.ID });
        req.RaceIDs = raceIds;
        req.CategoryIDs = categoryIds;
        req.GroupIDs = Groups.value.map((g) => g.ID);
        req.Parameters = [];
        req.Parameters.push(DynamicReportParameter.FromJsValues({ Name: 'ResultatType', StringValue: 'CLASS' }));
        req.Parameters.push(DynamicReportParameter.FromJsValues({ Name: 'Language', StringValue: 'norwegian' }));
        req.Parameters.push(DynamicReportParameter.FromJsValues({ Name: 'MaxRows', IntValue: 99999999 }));
        req.Parameters.push(DynamicReportParameter.FromJsValues({ Name: 'off', IntValue: type == 'OfficialPrinted' ? 1 : 0 }));

        const { stream, contentType } = await ReportDao.ReportFile(req, eventCommon.Event.value!.ID);

        if (stream == null) {
            useToastStore().addError('report.error_reading_report');
        }

        try {
            await showStreamInNewWindow(stream!, contentType);
        } catch (e) {
            useToastStore().addError((e as Error).message);
        }
    }, '');
};

/**
 * Print the unofficial results
 */
const printResultsAction = async (
    raceIds: number[],
    categoryIds: number[],
    type: EventImplementationType,
    row?: RaceControlItem | null,
) => {
    saveEventImplementations(type, undefined, undefined, row);

    const reportType = type == 'UnofficialPrinted' ? 'ResultListUnofficial' : 'ResultListOfficial';

    /**
     * Find the correct report for the type
     */
    const managerReport = ManagerReportsForEvent.value.find((r) => r.Reporttype == reportType);
    if (managerReport == null) {
        return null;
    }
    const report = Reports.value.find((r) => r.ID == managerReport.ReportID);

    if (!report) {
        console.warn('Could not find report for', type);
        return;
    }

    await printReport(raceIds, categoryIds, type, report);
};

/**
 * Set the official status on EQ Live
 * @param official
 */
const setOffical = (official: boolean) => saveEventImplementations('OfficialLive', new Date(), official);

/**
 * Set the appeal time
 * Will show a dialog to set the appeal time
 */
const setAppealtimeAction = () => {
    const timemodel = ref(new Date());
    // We need to pop up a Save Cancel dialog with a date time picker
    useDialog().SaveCancel({
        title: 'racecontrol.setappealtime',
        body: InputDateTime,
        vmodel: timemodel,
        saveCallback: async () => {
            if (timemodel.value == null) {
                console.warn('User tried to save appealtime as null. This should be impossible.');
                return false;
            }

            // Appealtime should be the time from the input and the current date.
            const appealTime = addTimeToDate(timemodel.value, new Date());
            await saveEventImplementations('AppealDeadline', appealTime);

            return true;
        },
        componentProps: { format: 'TIME' },
        options: {
            Size: 'md',
        },
    });
};

/**
 * Whenever the data has changed we need to save it
 */
const saveData = async () => {
    // TODO: We need to find all the data that has changed.
    if (!isChanged.value) {
        useToastStore().addInfo('general.nothingtosave');
        return;
    }

    rows.value.forEach(async (row) => {
        if (
            row.AppealDeadline?.EntityState != null &&
            (row.AppealDeadline?.EntityState == 'Modified' || row.AppealDeadline?.EntityState == 'Added')
        ) {
            await EventImplementationDao.SaveImplementations(eventID.value, [row.AppealDeadline]);
        }
        if (
            row.OfficialUnoffical != null &&
            (row.OfficialUnoffical?.EntityState == 'Modified' || row.OfficialUnoffical?.EntityState == 'Added')
        ) {
            await EventImplementationDao.SaveImplementations(eventID.value, [row.OfficialUnoffical]);
        }
    });
    isChanged.value = false;
};

/**
 * Handling the EventImplementation items
 * @param item
 * @param type
 * @param categoryId
 * @param raceId
 * @param time
 * @param bool
 */
const UpdateEventImplementationItem = (
    type: EventImplementationType,
    categoryId: number,
    raceId: number,
    item?: EventImplementation | null,
    time?: Date | null,
    bool?: boolean,
) => {
    if (item != null) {
        item.TimeValue = time ?? new Date();
        item.BooleanValue = bool ?? null;
    } else {
        item = EventImplementation.FromJsValues({
            EntityState: 'Added',
            ID: useUtilStore().nextFakeId(),
            TimeValue: time ?? new Date(),
            BooleanValue: bool ?? null,
        });
    }

    item.EventID = eventID.value;
    item.CategoryID = categoryId;
    item.RaceID = raceId;
    item.TypeID = type;

    return item;
};

/**
 * Save the event implementations with the correct type
 * @param type
 * @param time If we want
 */
const saveEventImplementations = async (
    type: EventImplementationType,
    time?: Date | null,
    bool?: boolean,
    row?: RaceControlItem | null,
) => {
    let selectedRows = row ? [row] : (grid.value?.getSelectedRows() ?? []);

    if (selectedRows.length == 0) {
        return;
    }

    let eventImplementations: EventImplementation[] = [];

    selectedRows.forEach((x) => {
        let item: EventImplementation | null = null;
        if (type == 'OfficialPrinted') {
            item = x.OfficalPrinted;
        } else if (type == 'UnofficialPrinted') {
            item = x.UnofficialPrinted;
        } else if (type == 'AppealDeadline') {
            item = x.AppealDeadline;
        } else if (type == 'OfficialLive') {
            item = x.OfficialUnoffical;
        }

        if (item == null) {
            item = EventImplementation.FromJsValues({
                EntityState: 'Added',
            });

            // We assign the item to the correct property so it immidiately shows up in the grid
            x.AppealDeadline = type == 'AppealDeadline' ? item : x.AppealDeadline;
            x.OfficalPrinted = type == 'OfficialPrinted' ? item : x.OfficalPrinted;
            x.UnofficialPrinted = type == 'UnofficialPrinted' ? item : x.UnofficialPrinted;
            x.OfficialUnoffical = type == 'OfficialLive' ? item : x.OfficialUnoffical;
        }

        UpdateEventImplementationItem(type, x.CategoryID, x.RaceID, item, time, bool);
        eventImplementations.push(item);
    });

    grid.value?.refreshCachedData();

    await EventImplementationDao.SaveImplementations(eventID.value, eventImplementations);
};

// Contains the roles that are finish stations. This is only so we dont have to loop through all the roles every time we get a new time
const finishStationRoles = computed(() => new Set<number>(StationRoles.value.filter((x) => x.Finish).map((x) => x.ID)));

/**
 * Whenever the rows change we need to refresh the grid
 * @remark This should not really happen other than when we get more race category combinations
 */
watch(computedRows, () => grid.value?.refreshCachedData());

/**
 * Whenever the categories, contestants or active times change we need to rebuild the dataset
 */
watchDebounced(
    [Categories, Races, ContestantsIsLoaded, EventImplementations],
    () => {
        console.log('Rebuilding dataset due to changes in categories, contestants or active times');
        buildDataset();
    },
    { immediate: true, debounce: 1500 },
);

const timesToProcess = new Set<RaceContestantTime>();

const timeDebouncer = useDebouncer();

/**
 * Handle new active times
 */
useRaceContestantTimesSubscription(
    (newValue: RaceContestantTime[]) => {
        // We need to update the data with the new active times

        newValue.forEach((time) => {
            timesToProcess.add(time);
        });

        // We need to debounce all the times that comes and then recalculate the NumberOfDNX and NumberOfFinished from that.
        timeDebouncer(() => {
            let refresh = false;

            // Create a new copy and then clear the set
            const _times = timesToProcess.size > 0 ? Array.from(timesToProcess) : [];
            timesToProcess.clear();

            const uniqueRowIds = new Set<string>();

            _times.forEach((time) => {
                // First check if it is a finish station
                if (!finishStationRoles.value.has(time.StationRoleID)) {
                    return;
                }

                const contestant = contestantStore.GetContestantByRaceContestantId(time.RaceContestantID);
                const raceContestant = contestant?.RaceContestants.find((rc) => rc.ID == time.RaceContestantID);

                if (raceContestant == null) {
                    return;
                }

                const rowId = `${raceContestant.RaceID}-${raceContestant.CategoryID}`;
                uniqueRowIds.add(rowId);
            });

            refresh = uniqueRowIds.size > 0;

            // We need to loop every unique key and get the raceContestants for those data
            uniqueRowIds.forEach((rowId) => {
                const row = rows.value.find((x) => x.ID == rowId);

                if (row != null) {
                    // We need to rebuild the row
                    const [raceIdStr, categoryIdStr] = rowId.split('-');
                    const raceId = parseInt(raceIdStr);
                    const categoryId = parseInt(categoryIdStr);

                    const rowdetails = getRowdataDetails(raceId, categoryId);
                    row.NumContestants = rowdetails.numberOfContestants;
                    row.NumberOfStarted = rowdetails.numberOfStarted;
                    row.NumberOfDNX = rowdetails.numberOfDnx;
                    row.NumberOfFinished = rowdetails.numberOfFinished;
                    row.LastFinished = rowdetails.lastFinished;
                    row.StartingTimes = Array.from(rowdetails.startingTimes).sort((a, b) => a.getTime() - b.getTime());

                    // if the NumberOfFinished + NumberOfDNX == NumberOfStarted then we need to emit the categoryCompleted event
                    if (row.NumberOfFinished + row.NumberOfDNX == row.NumberOfStarted && row.NumberOfStarted > 0) {
                        emits('categoryCompleted', row);
                    }
                }
            });

            if (refresh) {
                grid.value?.refreshCachedData();
            }

            // Make sure we have the latest data
        }, 200);
    },
    { triggerForExistingTimes: true },
);

// Debouncer for batching contestant updates
const contestantDebouncer = useDebouncer();

// We batch some updates to avoid flooding the grid with updates
const contestantsToProcess = new Set<ContestantBase>();

/**
 * Listen for changes to the contestants
 */
useContestantUpdateSubscription((contestants: ContestantBase[]) => {
    contestants.forEach((contestant) => {
        contestantsToProcess.add(contestant);
    });

    contestantDebouncer(() => {
        const _contestants = contestantsToProcess.size > 0 ? Array.from(contestantsToProcess) : [];
        contestantsToProcess.clear();

        const refreshRowIds = new Set<string>();
        // We need to update all the rows that the contastans racecontestants matches
        for (const contestant of _contestants) {
            for (const raceContestant of contestant.RaceContestants) {
                const rowId = `${raceContestant.RaceID}-${raceContestant.CategoryID}`;
                const row = rows.value.find((x) => x.ID == rowId);
                refreshRowIds.add(rowId);

                if (row != null) {
                    // We need to rebuild the row
                    const rowdetails = getRowdataDetails(raceContestant.RaceID, raceContestant.CategoryID);
                    row.NumContestants = rowdetails.numberOfContestants;
                    row.NumberOfStarted = rowdetails.numberOfStarted;
                    row.NumberOfDNX = rowdetails.numberOfDnx;
                    row.NumberOfFinished = rowdetails.numberOfFinished;
                    row.LastFinished = rowdetails.lastFinished;
                    row.StartingTimes = Array.from(rowdetails.startingTimes).sort((a, b) => a.getTime() - b.getTime());

                    // if the NumberOfFinished + NumberOfDNX == NumberOfStarted then we need to emit the categoryCompleted event
                    if (row.NumberOfFinished + row.NumberOfDNX == row.NumberOfStarted && row.NumberOfStarted > 0) {
                        emits('categoryCompleted', row);
                    }
                }
            }
        }

        grid.value?.refreshCachedData();

        if (refreshRowIds.size == 0) {
            return;
        }
        grid.value?.refreshCells(Array.from(refreshRowIds.values()));
    }, 500);
});

/**
 * The toolbar actions
 */
const toolbarActions = computed(() => [
    DropdownItemButton.create({
        title: hideRowsWithoutContestants.value ? 'general.showall' : 'racecontrol.hiderowswithoutcontestants',
        icon: hideRowsWithoutContestants.value ? 'fa-eye' : 'fa-eye-slash',
        class: hideRowsWithoutContestants.value ? 'active' : '',
        buttonType: 'Secondary',
        action: () => {
            hideRowsWithoutContestants.value = !hideRowsWithoutContestants.value;
        },
    }),

    DropdownItemMenu.create({
        icon: 'fa-cog',
        dropdownItems: [
            DropdownItemButton.create({
                translationkey: 'racecontrol.printofficialresults',
                icon: 'fa-print',
                action: () => {
                    const { raceIds, categoryIds } = getRaceAndCategoryIdsFromSelectedRows();
                    printResultsAction(raceIds, categoryIds, 'OfficialPrinted');
                },
            }),
            DropdownItemButton.create({
                translationkey: 'racecontrol.printunofficialresults',
                icon: 'fa-print',
                action: () => {
                    const { raceIds, categoryIds } = getRaceAndCategoryIdsFromSelectedRows();
                    printResultsAction(raceIds, categoryIds, 'UnofficialPrinted');
                },
            }),
            DropdownItemButton.create({
                translationkey: 'racecontrol.setappealtime',
                icon: 'fa-bullhorn',
                action: () => setAppealtimeAction(),
            }),
            DropdownItemSeparator.create(),
            DropdownItemButton.create({
                translationkey: 'racecontrol.setliveofficial',
                icon: 'fa-badge-check',
                action: () => setOffical(true),
            }),

            DropdownItemButton.create({
                translationkey: 'racecontrol.setliveunofficial',
                icon: 'fa-badge',
                action: () => setOffical(false),
            }),
        ],
    }),
]);

/**
 * Helper function to get the the raceIds and categoryIds from the selected rows
 */
const getRaceAndCategoryIdsFromSelectedRows = (): { raceIds: number[]; categoryIds: number[] } => {
    const selectedRows: RaceControlItem[] = grid.value?.getSelectedRows() ?? [];

    const raceIds: number[] = [];
    const categoryIds: number[] = [];

    for (const row of selectedRows) {
        raceIds.push(row.RaceID);
        categoryIds.push(row.CategoryID);
    }
    return { raceIds, categoryIds };
};

/**
 * The toolbar actions on the right side
 */
const toolbarRightActions = [
    // Hide devices with no event
    DropdownItemButton.create({
        title: 'general.refresh',
        buttonType: 'Secondary',
        icon: 'fa-sync',
        action: () => loadData(),
    }),
];

const isVisible = ref(false);

/**
 * The element visibility callback
 * We use this one to handle the keyboard shortcuts. We register a CTRL+S shortcut when the element is visible and remove it when it is not
 * @param state
 */
const onElementVisibility = (state: boolean) => {
    isVisible.value = state;
    if (state) {
        useKeyboardListener().addKeyboardShortcut({
            alias: 'racecontrol',
            on: 'CTRL+S',
            action: () => {
                saveData();
            },
        });

        grid.value?.refreshCachedData();
    } else {
        useKeyboardListener().removeKeyboardShortcut('racecontrol');
    }
};

onMounted(() => {
    // We need to load the data
    loadData();

    EnableRaceControl();
});
</script>

<template>
    <ContentComp
        class="h-100"
        :load-state="LoadState"
        :show-as-modal="hasLoadedData"
        :quiet-load="hasLoadedData"
        v-element-visibility="onElementVisibility"
    >
        <AlertMessage
            ref="alertmessageElement"
            v-if="isChanged"
            type="warning"
            >{{ $tm('general.unsavedchanges') }}</AlertMessage
        >

        <ToolbarComp
            :search-box-placeholder="$tm('devicestatus.searchplaceholder')"
            v-model:searchtext="searchText"
        >
            <template #first>
                <ButtonGroup :actions="toolbarActions" />
                <ButtonComp
                    button-type="Save"
                    @click="saveData"
                />
            </template>
            <template #last>
                <ButtonGroup :actions="toolbarRightActions" />
            </template>
        </ToolbarComp>

        <GridComp
            ref="grid"
            :grid-setup="gridSetup"
            style="min-height: 20vh"
        />

        <Teleport to="body">
            <LoadingComp
                :state="PrintState"
                :show-as-modal="true"
                :confirm-close="false"
                style="z-index: 10000"
                class="position-absolute top-0"
            />
        </Teleport>
    </ContentComp>
</template>
